/**
 * Sticky "Run now" footer for the automation record.
 *
 * Self-gates to null for every record that is not a saved, unmanaged
 * `ahoy_scheduled_automation` with no SaveBar up, so it can be mounted
 * unconditionally in a surface's pinned footer slot — the slideout chrome's
 * `ReadyFooter` (record-slideout-regions.tsx) alongside the other sticky
 * footers, and the builder panel's frame (BuilderPanel.tsx) — without
 * affecting other surfaces. It renders in that footer slot, not inside
 * RecordBody's scrollable content, so it stays visible while the body
 * scrolls.
 *
 * Both hooks (`useRecordDraft`, `useRecordView`) run before the early-return
 * guard (Rules of Hooks). The exported `automationRunFooterApplies` predicate
 * is the single source of truth for applicability — RecordBody reuses it for
 * its scroll-column padding decision so the two can never diverge.
 */

/* eslint-disable react-refresh/only-export-components -- predicate and component are intentionally co-located: they share the same applicability contract */

import type { ReactElement } from 'react';

import { deriveAutomationCapabilities } from '@/lib/crm/automations/capabilities';
import { SCHEDULED_AUTOMATION_ENTITY_TYPE } from '@/lib/crm/automations/runNow';
import { RunNowButton } from '@/lib/crm/automations/RunNowButton';
import type { CrmObject } from '@/types/crm';

import {
  type RecordDraftValue,
  useRecordDraft,
} from '../contexts/RecordDraftContext';
import { useRecordView } from '../contexts/RecordViewContext';
import type { SaveBarState } from '../types';

/**
 * Whether the Run-now footer belongs on this record right now. The single
 * source of truth for BOTH the footer's own render gate and RecordBody's
 * scroll-column padding, so the two can never disagree.
 *
 * - Only saved, unmanaged scheduled automations can be run; an unsaved draft
 *   would 404.
 * - One bottom bar at a time: while a SaveBar is up (pending proposal in the
 *   builder, Settings edit in the slideout) Save/Cancel owns the bottom edge
 *   and Run now steps aside. This is what lets the footer sit inside the
 *   builder too — a saved automation viewed there with nothing pending is
 *   just the record, and Run now belongs on it.
 */
export function automationRunFooterApplies(
  object: CrmObject,
  draft: RecordDraftValue | null,
  saveBarState: SaveBarState | null
): boolean {
  return (
    object.entity_type_name === SCHEDULED_AUTOMATION_ENTITY_TYPE &&
    !deriveAutomationCapabilities(object).isManaged &&
    !draft?.isUnsaved &&
    saveBarState === null
  );
}

export function AutomationRunFooter({
  object,
}: {
  object: CrmObject;
}): ReactElement | null {
  const draft = useRecordDraft();
  const { saveBarState } = useRecordView();
  if (!automationRunFooterApplies(object, draft, saveBarState)) {
    return null;
  }
  return (
    <div className='shrink-0 px-4 pb-3' data-component='automation-run-footer'>
      <div className='flex justify-end'>
        <RunNowButton object={object} />
      </div>
    </div>
  );
}
